/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2024 (c) Daniel Littfinski, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2023-2024 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Nico Brandt, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { anyTodo, IMachine } from '@umati/dashboard-types';
import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import './Machine.scss';
import MachinesActions from './MachinesActions';
import { URLS } from 'Api/Api';

interface IMachineComponentState {
  editShortId: boolean;
  inputShortId: string;
}

interface IMachineInfoProps extends IMachine {
  markedForDelete?: boolean;
  shortIdEditRequested?: boolean;
}

interface IMachineDispatch {
  dispatch: Dispatch;
}

type IMachineComponentProps = IMachineInfoProps & IMachineDispatch;

class Machine extends Component<IMachineComponentProps, IMachineComponentState> {
  imgUpload: React.RefObject<HTMLInputElement | null>;

  constructor(props: IMachineComponentProps) {
    super(props);
    this.imgUpload = React.createRef<HTMLInputElement>();
    this.state = {
      editShortId: false,
      inputShortId: this.props.ShortId?.substr(3),
    };
  }

  handleChange(key: 'inputShortId') {
    const self = this;
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      self.setState({ [key]: event.target.value } as Pick<IMachineComponentState, typeof key>);
    };
  }

  delete() {
    MachinesActions.Delete(this.props.Id)(this.props.dispatch);
  }

  saveShortId() {
    MachinesActions.EditShortId(this.props.Id, `${this.state.inputShortId.substr(0, 10)}`)(this.props.dispatch);
    this.resetShortId();
  }

  resetShortId() {
    this.setState({
      editShortId: false,
    });
  }

  editShortId() {
    this.setState({
      editShortId: true,
      inputShortId: this.props.ShortId?.substr(3),
    });
  }

  imgUploadChange() {
    if (!this.imgUpload.current || !this.imgUpload.current.files || !this.imgUpload.current.files[0]) {
      return;
    }
    const file = this.imgUpload.current.files[0];
    if (file) {
      MachinesActions.UploadImg(this.props.Id, file)(this.props.dispatch);
    }
  }

  render() {
    // const identification = JSON.parse(this.props.data);
    const id = `machine-${this.props.Id}`;
    const idImgUpload = `${id}-imageUpload`;
    const identification = this.props.Data;
    const puri = JSON.stringify(identification?.ProductInstanceUri || 'None');
    return (
      <Card className="machine-card">
        <Card.Body>
          {this.props.ImagePath && <img src={URLS.Machine.getImage(this.props.ImagePath)} className="machine-image" />}
          To be deleted: {this.props.markedForDelete ? 'Yes' : 'No'}
          <br />
          Id: {this.props.Id}
          <br />
          MachineId: {this.props.MachineId}
          <br />
          ProductInstanceUri: {puri}
          <br />
          ShortId:
          {!this.state.editShortId && (
            <>
              {this.props.ShortId} {this.props.shortIdEditRequested && '...'}
              <Button onClick={() => this.editShortId()} variant="primary">
                Edit
              </Button>
            </>
          )}
          {this.state.editShortId && (
            <>
              {this.props.ShortId?.substr(0, 3)}
              <input
                type="text"
                value={this.state.inputShortId}
                onChange={this.handleChange('inputShortId')}
                className="short-id"
              />
              <Button onClick={() => this.saveShortId()} variant="success">
                Save
              </Button>
              <Button onClick={() => this.resetShortId()} variant="danger">
                Cancel
              </Button>
            </>
          )}
          <br />
          <Button onClick={() => this.delete()} variant="danger">
            Delete
          </Button>
          <input
            ref={this.imgUpload}
            type="file"
            id={idImgUpload}
            onChange={() => this.imgUploadChange()}
            accept=".jpg,.jpeg,.png"
            hidden
          />
          <label className="btn btn-primary" htmlFor={idImgUpload}>
            Image Upload
          </label>
        </Card.Body>
      </Card>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
  return {
    dispatch,
  };
};

const mapStateToProps = (state: anyTodo, ownProps: IMachineInfoProps) => {
  return {
    ...ownProps,
    markedForDelete: state.machines.requestedToDelete.includes(ownProps.Id),
    shortIdEditRequested: state.machines.requestedToEditShortId.includes(ownProps.Id),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Machine);
